/**
 * MediaPicture — a real product screenshot inside a browser-style frame.
 * WebP with a JPEG fallback, intrinsic width/height (no layout shift), lazy
 * loading unless `eager` (the hero / LCP image).
 */

import { SHOT_HEIGHT, SHOT_WIDTH, shotSrc, type ShotId } from '@/routes/landing/landing-media';

export function MediaPicture({
  id,
  alt,
  eager = false,
  className = '',
}: {
  id: ShotId;
  alt: string;
  eager?: boolean;
  className?: string;
}) {
  return (
    <picture>
      <source type="image/webp" srcSet={shotSrc(id, 'webp')} />
      <img
        src={shotSrc(id, 'jpg')}
        alt={alt}
        width={SHOT_WIDTH}
        height={SHOT_HEIGHT}
        loading={eager ? 'eager' : 'lazy'}
        decoding={eager ? 'sync' : 'async'}
        {...(eager ? { fetchPriority: 'high' as const } : {})}
        data-testid={`landing-shot-${id}`}
        className={`block h-auto w-full ${className}`}
      />
    </picture>
  );
}

/** Browser-window chrome around a screenshot. */
export function BrowserFrame({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`overflow-hidden rounded-token-xl border border-border bg-surface shadow-elevation-4 ${className}`}>
      <div aria-hidden="true" className="flex items-center gap-1.5 border-b border-border bg-surface-2/60 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      </div>
      {children}
    </div>
  );
}
